// components/ArtworkNotes/components/NotesFilter.tsx
import React, { useState, useEffect } from 'react';
import { NotesFilterStruct, NoteTypeDto, getNoteTypeIdByCode, PriorityOption } from '../../models/Note';
import { useReferenceData } from '../../components/ReferenceDataContext';
import { useNotesSafeRequests } from '../../api/useNotesApi';
import './NotesFilter.css';

// Define the filter structure without search
interface NotesFilterWithoutSearch {
    noteTypeId: number;
    tags: string[];
    priority: string;
}

interface NotesFilterProps {
    filters: NotesFilterWithoutSearch;
    onChange: (filters: NotesFilterWithoutSearch) => void;
    artworkId: number;
    api: any;
    disabled?: boolean;
    refreshTrigger?: number;
}

const NotesFilter: React.FC<NotesFilterProps> = ({
    filters,
    onChange,
    artworkId,
    api,
    disabled = false,
    refreshTrigger = 0
}) => {
    const { getTagsReq } = useNotesSafeRequests(api);
    const [availableTags, setAvailableTags] = useState<string[]>([]);
    const [loadingTags, setLoadingTags] = useState<boolean>(false);
    const [noteTypesRef, setNoteTypesRef] = useState<{ value: number; label: string }[]>([]);
    const { noteTypes, noteTypesState } = useReferenceData();

    //const noteTypes = [
    //    { value: '', label: 'All Types' },
    //    { value: NoteType.Installation, label: 'Installation' },
    //    { value: NoteType.Care, label: 'Care' },
    //    { value: NoteType.Transportation, label: 'Transportation & Shipping' },
    //    { value: NoteType.Conservation, label: 'Conservation & Restoration' },
    //    { value: NoteType.Exhibition, label: 'Exhibition or Loan' },
    //    { value: NoteType.General, label: 'General' }
    //];

    const priorityOptions: PriorityOption[] = [
        { value: '', label: 'All Priorities', color: '#6b7280' },
        { value: '0', label: 'Low Priority', color: '#6b7280' },
        { value: '1', label: 'Medium Priority', color: '#f59e0b' },
        { value: '2', label: 'High Priority', color: '#ef4444' }
    ];

    useEffect(() => {

        let mounted = true;

        (async () => {

            if (!mounted) return;

            if (noteTypesState.loading) return;

            if (noteTypesState.error) {
                return <div>Error: {noteTypesState.error}</div>;
            }

            if (!noteTypesState.data || noteTypesState.data.length === 0) {
                return <div>No note types available</div>;
            }

            if (noteTypes) {

                const options = noteTypes
                    .sort((a, b) => a.sortOrder !== b.sortOrder ? a.sortOrder - b.sortOrder : a.name.localeCompare(b.name))
                    .map(x => ({ value: x.noteTypeId, label: x.name }));

                setNoteTypesRef(options);
            }
        })();

        return () => { mounted = false; };
    }, []);

    useEffect(() => {
        loadPopularTags();
    }, [artworkId, refreshTrigger]);

    const loadPopularTags = async (): Promise<void> => {
        setLoadingTags(true);
        try {
            const tags = await getTagsReq.run({
                method: "GET",
                url: `/notes/tags`,
                params: { artworkId, count: 20 }
            });
            setAvailableTags(tags || []);
        } catch (error) {
            console.error('Failed to load tags:', error);
            setAvailableTags([]);
        } finally {
            setLoadingTags(false);
        }
    };

    const handleTypeChange = (noteTypeId: number): void => {
        onChange({
            ...filters,
            noteTypeId
        });
    };

    const handlePriorityChange = (priority: string): void => {
        onChange({
            ...filters,
            priority
        });
    };

    const handleTagToggle = (tag: string): void => {
        if (disabled) return;

        const currentTags = filters.tags || [];
        const newTags = currentTags.includes(tag)
            ? currentTags.filter(t => t !== tag)
            : [...currentTags, tag];

        onChange({
            ...filters,
            tags: newTags
        });
    };

    const clearFilters = (): void => {
        if (disabled) return;

        onChange({
            noteTypeId: 0,
            priority: '',
            tags: []
        });
    };

    const hasActiveFilters = filters.noteTypeId || filters.priority || (filters.tags && filters.tags.length > 0);

    return (
        <div className={`notes-filter ${disabled ? 'disabled' : ''}`}>
            <div className="filter-header">
                <h5>Filters</h5>
                {hasActiveFilters && (
                    <button
                        type="button"
                        onClick={clearFilters}
                        className="btn-clear"
                        disabled={disabled}
                    >
                        Clear All
                    </button>
                )}
            </div>

            <div className="filter-group">
                    <label htmlFor="note-type">Note Type</label>
                    <select
                        id="note-type"
                        value={filters.noteTypeId || 0}
                        onChange={(e) => handleTypeChange(Number(e.target.value))}
                        className="filter-select"
                        disabled={disabled}
                    >
                    {noteTypesRef.map(type => (
                            <option key={type.value} value={type.value}>
                                {type.label}
                            </option>
                        ))}
                    </select>
            </div>

            {/* Tags section - full width below */}
            <div className="filter-group">
                <label>
                    Tags
                    {loadingTags && <span className="loading-small">...</span>}
                </label>
                <div className="tags-filter">
                    {availableTags.map(tag => (
                        <button
                            key={tag}
                            type="button"
                            onClick={() => handleTagToggle(tag)}
                            className={`tag-filter ${filters.tags?.includes(tag) ? 'active' : ''}`}
                            disabled={disabled}
                        >
                            {tag}
                            {filters.tags?.includes(tag) && <span className="checkmark">✓</span>}
                        </button>
                    ))}
                    {availableTags.length === 0 && !loadingTags && (
                        <div className="no-tags">No tags available</div>
                    )}
                </div>
            </div>

            {hasActiveFilters && (
                <div className="active-filters">
                    <span>Active filters:</span>
                    {filters.noteTypeId != 0 && (
                        <span className="active-filter">
                            Type: {filters.noteTypeId}
                            <button
                                type="button"
                                onClick={() => handleTypeChange(0)}
                                className="remove-filter"
                                disabled={disabled}
                            >
                                ×
                            </button>
                        </span>
                    )}
                    {filters.tags?.map(tag => (
                        <span key={tag} className="active-filter">
                            Tag: {tag}
                            <button
                                type="button"
                                onClick={() => handleTagToggle(tag)}
                                className="remove-filter"
                                disabled={disabled}
                            >
                                ×
                            </button>
                        </span>
                    ))}
                </div>
            )}
        </div>
    );
};

export default NotesFilter;